import { ITree } from "Grids/Interfaces";
import { BaseGrid } from "./BaseGrid";
import { GridSettings } from "Configuration";
import { CommandService, TextBlockService, DropDownService, GridDefinitionService, DatabaseImageService } from "Service";
import { IODataResult, IDataRow } from "Entities";
import { TreeLinkTypes } from "Enums";
import { Navigation, Util } from "Core";
import { EventArgs } from "Core/Events";
import { DataConstants } from "Constants";
import { GridEventTypes } from "Core/Events/EventTypes";
import { Environment, TreeDefinition, TreeLinkDefinition } from "Service/Client";

/**
 * @hidden
 */
export const LOAD_IDENTIFIER_COLUMN_NAME: string = "__dms_load_identifier";

/**
 * ## Tree
 *
 * Showing hierarchical data as tree.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a Tree with the name GridName
 * // In script:
 * @@grd:GridName@@
 *
 * // On page:
 * $dms("GridName")
 *
 * // First Tree on page:
 * $dms(".dms-tree")
 * ```
 */
export class Tree extends BaseGrid implements ITree {
    private _kendoElement: kendo.ui.TreeView;

    private gridDefinition: TreeDefinition;
    private dataSourceOptions: kendo.data.DataSourceOptions;
    private contextMenu: kendo.ui.ContextMenu;
    private commandPrepDataItem: kendo.data.Node;
    private commandPrepName: string;
    private expandedElements: { [hash: string]: boolean } = {};

    private scrollPos = 0;

    private currentLoadIdentifier: number = 0;

    private loadIdentifiers: { [identifier: number]: number } = {};

    /**
     * @hidden
     */
    public static wrapperTemplate: string = require("./Templates/Tree.html").default;

    /**
     * Create a new Tree.
     * @param gridDefinition GridDefinition.
     * @param gridSettings GridSettings.
     * @hidden
     */
    constructor(
        gridDefinition: TreeDefinition,
        textBlockCategory: string,
        gridSettings: GridSettings,
        environment: Environment,
        comboboxService: DropDownService,
        commandService: CommandService,
        gridDefinitionService: GridDefinitionService,
        textBlockService: TextBlockService,
        databaseImageService: DatabaseImageService
    ) {
        super(
            gridDefinition,
            textBlockCategory,
            gridSettings,
            environment,
            comboboxService,
            commandService,
            gridDefinitionService,
            textBlockService,
            databaseImageService
        );
        this.gridDefinition = gridDefinition;
        this.isAutoResize = true;
    }

    /**
     * Sets the jQuery-Element to initialize the grid.
     * @param element JQuery-Wrapper in which the grid is initialized.
     * @hidden
     */
    protected async setElementInternal(element: JQuery): Promise<void> {
        await this.textBlockService.getAll();

        this.dataSourceOptions = {
            type: "odata",
            transport: {
                read: (options: kendo.data.DataSourceTransportRead) => {
                    // get rowGuid of current node
                    let rowGuid = options.data[DataConstants.ROW_GUID_COLUMN_NAME];

                    let currentLoadIdentifier: number;
                    if (options.data[LOAD_IDENTIFIER_COLUMN_NAME] != null) {
                        currentLoadIdentifier = options.data[LOAD_IDENTIFIER_COLUMN_NAME];
                    } else {
                        let nodeModel = this._kendoElement.dataSource.get(rowGuid);
                        currentLoadIdentifier = nodeModel.get(LOAD_IDENTIFIER_COLUMN_NAME);
                    }
                    this.loadIdentifiers[currentLoadIdentifier]++;

                    this.commandService
                        .getTree(this.gridSettings.dataUrlGet, this.gridDefinition.GridID, rowGuid, this.getParentGuid(rowGuid))
                        .then((result) => {
                            if (currentLoadIdentifier == this.currentLoadIdentifier) {
                                this.storeOriginalDataRow(result.value);
                                for (let idx = 0, len = result.value.length; idx < len; idx++) {
                                    let row = result.value[idx];
                                    row[LOAD_IDENTIFIER_COLUMN_NAME] = currentLoadIdentifier;
                                    // expand if node was expanded before treereload
                                    if (this.gridDefinition.ExpandedColumnName) {
                                        row.expanded = row[this.gridDefinition.ExpandedColumnName];
                                    }
                                    if (this.expandedElements.hasOwnProperty(row[DataConstants.ROW_HASH_COLUMN_NAME])) {
                                        row.expanded = this.expandedElements[row[DataConstants.ROW_HASH_COLUMN_NAME]];
                                    }
                                }

                                (options.success as JQuery.Ajax.SuccessCallback<any>)(result, null, null);
                            } else {
                                this.loadIdentifiers[currentLoadIdentifier]--;
                            }
                        });
                },
            },
            schema: {
                data: function (data: IODataResult) {
                    return data.value;
                },
                total: function (data: IODataResult) {
                    return data["odata.count"];
                },
                model: {
                    id: DataConstants.ROW_GUID_COLUMN_NAME,
                    hasChildren: this.gridDefinition.HasChildNodesColumnName,
                },
            },
        };

        this._kendoElement = new kendo.ui.TreeView(this.jQueryElement[0], {
            animation: {
                expand: {
                    effects: "none",
                },
                collapse: {
                    effects: "none",
                },
            },
            dataSource: new kendo.data.HierarchicalDataSource(this.dataSourceOptions),
            dataTextField: this.gridDefinition.NodeTextColumnName,
            // select: (e) => this.onSelect(e),
            dataBound: (e) => this.onDataBound(e),
            messages: {
                loading: this.gridDefinition.NodesLoadingCaption
                    ? this.textBlockService.get(this.gridDefinition.NodesLoadingCaption, this.textBlockCategory)
                    : "",
            },
            autoBind: false,
            template: $.proxy(kendo.template(`#= this.formatNode(data.item) || "" #`, { useWithBlock: false }), this),
            expand: (e) => this.onExpand(e),
            collapse: (e) => this.onCollapse(e),
        });
        this.jQueryElement.on("click", ".k-treeview-leaf", (e) => this.onClick(e));
    }

    /**
     * Triggers when Node is expanded
     * @param e
     */
    private onExpand(e: kendo.ui.TreeViewExpandEvent): void {
        this.expandedElements[this._kendoElement.dataItem(<JQuery>$(e.node)).get(DataConstants.ROW_HASH_COLUMN_NAME)] = true;
    }

    /**
     * Triggers when Node is collapsed
     * @param e
     */
    private onCollapse(e: kendo.ui.TreeViewCollapseEvent): void {
        this.expandedElements[this._kendoElement.dataItem(<JQuery>$(e.node)).get(DataConstants.ROW_HASH_COLUMN_NAME)] = false;
    }

    /**
     * Get the parent RowGuid for the current item.
     * @param rowGuid RowGuid of the current item.
     */
    private getParentGuid(rowGuid: string): string {
        // get parentRowGuid. the server needs to know in which dataset to look for the row.
        let parentRowGuid: string = null;
        if (this._kendoElement != null) {
            // tree is not initialized yet in first request
            // get current node
            let nodeModel = this._kendoElement.dataSource.get(rowGuid);
            if (nodeModel) {
                let node = this._kendoElement.findByUid(nodeModel.uid);
                // get parent node
                let parent = this._kendoElement.parent(node);
                let dataItem = this._kendoElement.dataItem(parent);
                if (dataItem) {
                    parentRowGuid = dataItem.get(DataConstants.ROW_GUID_COLUMN_NAME);
                }
            }
        }
        return parentRowGuid;
    }

    /**
     * Triggers after data is loaded
     * @param e
     */
    private onDataBound(e: kendo.ui.TreeViewDataBoundEvent): void {
        let loadIdentifier;
        if (e.node) {
            let dataItem = this._kendoElement.dataItem(e.node);
            loadIdentifier = dataItem.get(LOAD_IDENTIFIER_COLUMN_NAME);
        } else {
            loadIdentifier = this.currentLoadIdentifier;
        }

        this.loadIdentifiers[loadIdentifier]--;

        // setTimeout(() => {
        if (e.node) {
            if (e.node.attr("data-dms-expand-all")) {
                this.expandAllChildren(e.node);
            }
        }

        if (this.loadIdentifiers[loadIdentifier] == 0) {
            this.createContextMenu();
            // });
            if (this.scrollPos > 0) {
                this._kendoElement.element.scrollTop(this.scrollPos);
                this.scrollPos = 0;
            }
            this.setBusy(false);
            this.trigger(GridEventTypes.DataBound);
        }
    }

    /**
     * Expands all children of a node.
     * @param node Node which should be expanded.
     * @since 4.0.0
     */
    public expandAllChildren(node: JQuery) {
        node.attr("data-dms-expand-all", "true");
        this._kendoElement.expand(node.addBack());
        let childNodes = node.find("> .k-treeview-group > li.k-treeview-item");
        if (childNodes) {
            for (let i = 0, len = childNodes.length; i < len; i++) {
                $(childNodes[i]).prop("dms-expand-all", true);
                this._kendoElement.expand($(childNodes[i]).addBack());
            }
        }
    }

    /**
     * Triggers if Entry in ContextMenu is clicked
     * @param e
     * @param treeLinksContext
     */
    private async onContextMenuSelect(e: kendo.ui.ContextMenuSelectEvent, treeLinksContext: TreeLinkDefinition[]): Promise<void> {
        let treeLinkDefinition = treeLinksContext[Number($(e.item).attr("dms-treelinkdefinitionid"))];
        this.processTreeLink(treeLinkDefinition, <JQuery>$(e.target).parent().parent());
    }

    /**
     * Creates all ContextMenus for Tree
     * @param nodes
     * @param nodeFunc
     */
    private createContextMenu(): void {
        if (this.contextMenu) {
            this.contextMenu.destroy();
        }
        let options: kendo.ui.ContextMenuOptions = {
            target: this.jQueryElement,
            alignToAnchor: true,
            filter: ".k-treeview-leaf",
            open: (e) => this.onOpen(e),
            copyAnchorStyles: false,
        };
        this.contextMenu = new kendo.ui.ContextMenu($(`<ul class="dms-context-menu">`)[0], options);
    }

    /**
     * Handles the select-event. Select on Node.
     * @param e EventArgs.
     */
    private onClick(e: JQuery.ClickEvent) {
        let node = $(e.target);
        let dataItem = this._kendoElement.dataItem(node);

        let treeLinks = this.getTreeLinkDefinitions(true, dataItem, true);
        if (treeLinks && treeLinks.length > 0) {
            let treeLinkDefinition = treeLinks[0];
            this.processTreeLink(treeLinkDefinition, node);
        }
    }

    /**
     * Triggers after right click onto tree
     * @param e
     * @param nodes
     */
    private onOpen(e: kendo.ui.ContextMenuOpenEvent) {
        let row = $(e.target);
        let dataItem = this._kendoElement.dataItem(<JQuery>row);
        let jQueryNode = this._kendoElement.findByUid(dataItem.uid);

        let treeLinksContext = this.getTreeLinkDefinitions(false, dataItem, false);
        if (treeLinksContext.length > 0) {
            let data = this.createDataSourceObjects({ dataItem: jQueryNode, treeLinksContext });

            this.contextMenu.setOptions({
                dataSource: data.dataSourceObjects,
                select: (e: kendo.ui.ContextMenuSelectEvent) => this.onContextMenuSelect(e, treeLinksContext),
            });

            for (let i = 0, len = data.disabledObjects.length; i < len; i++) {
                if (this.gridDefinition.RemoveDisabledContextMenuItems) {
                    // remove
                    this.contextMenu.remove($("[dms-treelinkdefinitionid=" + data.disabledObjects[i] + "]"));
                } else {
                    //disable
                    this.contextMenu.enable($("[dms-treelinkdefinitionid=" + data.disabledObjects[i] + "]"), false);
                }
            }
        } else {
            e.preventDefault();
        }
    }

    /**
     * Creates SourceObjects for ContextMenu
     * @param dataItem
     * @param treeLinksContext
     */
    private createDataSourceObjects({ dataItem, treeLinksContext }: { dataItem: any; treeLinksContext: TreeLinkDefinition[] }): {
        dataSourceObjects: Array<Object>;
        disabledObjects: Array<number>;
    } {
        let dataSourceObjects = [];
        let disabledObjects = [];
        for (let i = 0, len = treeLinksContext.length; i < len; i++) {
            let treeLink = treeLinksContext[i];

            if (treeLink.TypeID == TreeLinkTypes.Command && treeLink.CommandName && treeLink.CommandName != this.commandPrepName) {
                disabledObjects.push(i);
            }

            dataSourceObjects.push({
                text: this.textBlockService.get(treeLink.Caption, this.textBlockCategory),
                attr: {
                    "dms-treelinkdefinitionid": i,
                },
            });
        }
        return { dataSourceObjects, disabledObjects };
    }

    /**
     * Format a node based on it's {TreeLinkDefinition}.
     * @param node Node to be formatted.
     * @hidden
     */
    public formatNode(node: kendo.data.Node): string {
        let treeLinks = this.getTreeLinkDefinitions(true, node, true);
        let template = "";
        if (treeLinks.length > 0) {
            let treeLink = treeLinks[0];
            if (treeLink.Icon) {
                if (treeLink.Icon.indexOf(".") >= 0 && treeLink.Icon.indexOf("#") == -1) {
                    template += "<img src='" + this.environment.ImagePath + treeLink.Icon + "'/>&nbsp;";
                } else {
                    template += `<span class="${this.resolve(treeLink.Icon, <any>node)}"></span>&nbsp;`;
                }
            }
            if (treeLink.CssClass) {
                template += `<span class="${this.resolve(treeLink.CssClass, <any>node)}">`;
            }
            template += node.get(this.gridDefinition.NodeTextColumnName);
            if (treeLink.CssClass) {
                template += "</span>";
            }
        } else {
            template += node.get(this.gridDefinition.NodeTextColumnName);
        }
        return template;
    }

    /**
     * Get the TreeLinkDefinitions for the current tree node.
     * @param isDefault True for default click handler, false for contextmenu.
     * @param dataItem The dataRow of the current node.
     * @param onlyFirst True if only the first matching link should be returned.
     */
    private getTreeLinkDefinitions(isDefault: boolean, dataItem: kendo.data.Node, onlyFirst?: boolean): TreeLinkDefinition[] {
        let treeLinks: TreeLinkDefinition[] = [];
        for (let i = 0, len = this.gridDefinition.TreeLinkDefinitions.length; i < len; i++) {
            let treeLinkDefinition = this.gridDefinition.TreeLinkDefinitions[i];
            if (treeLinkDefinition.IsDefault === isDefault || (!isDefault && !treeLinkDefinition.IsDefault)) {
                if (treeLinkDefinition.RangeColumnName) {
                    if (
                        Util.isValueInRange(
                            dataItem.get(treeLinkDefinition.RangeColumnName),
                            treeLinkDefinition.RangeFrom,
                            treeLinkDefinition.RangeTo
                        )
                    ) {
                        treeLinks.push(treeLinkDefinition);
                        if (onlyFirst) {
                            break;
                        }
                    }
                } else {
                    treeLinks.push(treeLinkDefinition);
                    if (onlyFirst) {
                        break;
                    }
                }
            }
        }
        return treeLinks;
    }

    /**
     * Process treeLink action.
     * @param treeLinkDefinition
     * @param dataItem
     */
    private async processTreeLink(treeLinkDefinition: TreeLinkDefinition, node: JQuery<HTMLElement>): Promise<void> {
        let dataItem = this._kendoElement.dataItem(node);
        let rowGuid = dataItem.get(DataConstants.ROW_GUID_COLUMN_NAME);

        switch (treeLinkDefinition.TypeID) {
            case TreeLinkTypes.Link: {
                if (treeLinkDefinition.Link) {
                    if (treeLinkDefinition.LinkMode) {
                        Navigation.openWindow(
                            super.resolve(treeLinkDefinition.Link, <IDataRow>(<any>dataItem)),
                            treeLinkDefinition.LinkMode
                        );
                    } else {
                        Navigation.navigate(super.resolve(treeLinkDefinition.Link, <IDataRow>(<any>dataItem)));
                    }
                }
                break;
            }
            case TreeLinkTypes.Script: {
                if (treeLinkDefinition.Script) {
                    let eventArgs = new EventArgs(this, node);
                    this.eventHandler.executeScript(super.resolve(treeLinkDefinition.Script, <IDataRow>(<any>dataItem)), eventArgs);
                }
                break;
            }
            case TreeLinkTypes.Command: {
                if (treeLinkDefinition.ConfirmText) {
                    let text = this.textBlockService.get(treeLinkDefinition.ConfirmText, this.textBlockCategory);

                    let execute = await Navigation.confirmAsync(
                        super.resolve(text, {}),
                        this.textBlockService.get("pageTitle", this.textBlockCategory)
                    );

                    if (!execute) {
                        return;
                    }
                }
                let eventArgs = new EventArgs(this, node);

                if (treeLinkDefinition.Script) {
                    // is there a script
                    this.eventHandler.executeScript(super.resolve(treeLinkDefinition.Script, <IDataRow>(<any>dataItem)), eventArgs);
                }
                if (treeLinkDefinition.IsCommand && !treeLinkDefinition.CommandName) {
                    // should command be executed
                    this.commandService
                        .treeLinkCommand(this.gridDefinition.GridID, treeLinkDefinition.TreeLinkID, rowGuid, this.getParentGuid(rowGuid))
                        .then(() => {
                            this.trigger(GridEventTypes.TreeLinkCommandExecuted, eventArgs);
                        });
                } else if (
                    treeLinkDefinition.IsCommand &&
                    treeLinkDefinition.CommandName &&
                    this.commandPrepName == treeLinkDefinition.CommandName &&
                    this.commandPrepDataItem
                ) {
                    let fromRowGuid = this.commandPrepDataItem.get(DataConstants.ROW_GUID_COLUMN_NAME);
                    this.commandService
                        .treeLinkCommand(
                            this.gridDefinition.GridID,
                            treeLinkDefinition.TreeLinkID,
                            rowGuid,
                            this.getParentGuid(rowGuid),
                            fromRowGuid,
                            this.getParentGuid(fromRowGuid)
                        )
                        .then(() => {
                            this.trigger(GridEventTypes.TreeLinkCommandExecuted, eventArgs);
                        });
                }
                break;
            }
            case TreeLinkTypes.Commandprep: {
                this.commandPrepDataItem = dataItem;
                this.commandPrepName = treeLinkDefinition.CommandName;
                break;
            }
        }
    }

    /**
     * Get the kendo treeview control. Click {@link http://docs.telerik.com/kendo-ui/api/javascript/ui/treeview|here} for the api definition.
     * @since 4.0.0
     */
    public getKendoElement(): kendo.ui.TreeView {
        return this._kendoElement;
    }

    /**
     * Set the height of the tree.
     * @param height Height in pixel.
     * @since 4.0.0
     */
    public setHeight(height: string | number): void {
        super.setHeight(height);
        this._kendoElement.wrapper.height(height);
        this._kendoElement.resize();
    }

    /**
     * Reloads data of this grid.
     * @hidden
     */
    protected reloadInternal(): Promise<void> {
        this.setBusy(true);
        this.scrollPos = this._kendoElement.element.scrollTop();
        this.show();
        this.currentLoadIdentifier++;
        this.loadIdentifiers[this.currentLoadIdentifier] = 0;
        return Promise.resolve(this._kendoElement.dataSource.read({ [LOAD_IDENTIFIER_COLUMN_NAME]: this.currentLoadIdentifier }));
    }

    /**
     * Clears the data of the grid.
     * @since 4.0.0
     */
    public clear(): void {
        if (this._kendoElement) {
            if (this._kendoElement.dataSource.data().length > 0) {
                this.loadIdentifiers[this.currentLoadIdentifier]++;
                this._kendoElement.dataSource.data([]);
            }
        }
    }

    /**
     * Dispose the grid and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();

        if (this._kendoElement) {
            this._kendoElement.destroy();
        }
    }

    /**
     * Gets the current edited (or added) data row.
     * @since 4.5.0
     */
    public getCurrentDataRow(): IDataRow {
        return undefined;
    }

    /**
     * Set the tree busy.
     *
     * @param isBusy Pass true to show, false to hide the busy indicator.
     * @since 4.0.0
     */
    public setBusy(isBusy: boolean): void {
        kendo.ui.progress(this.jQueryElement.parent(), isBusy);
    }
}
